/**
 * Per-day CTA state for the weekly order view's day cards.
 *
 * Decides what the card header affords:
 *  - 'service-details' → History mode (PROD-1123 v2) — the day is strictly
 *                  past (or its cafeteria service is complete) and saw any
 *                  service-relevant activity: school orders, parent orders,
 *                  or a completed cafeteria service (which covers
 *                  register-only days). The card shows a service badge and
 *                  a "View service details" link to /orders/school/service.
 *                  History cards never expand in place. Gating on school
 *                  orders alone would hide the page from parent-order-only
 *                  campuses — exactly the POS-heavy shape PROD-1123 is for.
 *  - 'details'   → "Show details" — the day has orders to review (kept even
 *                  when the day has since closed or its menu was archived),
 *                  or the surface is read-only and the menu is viewable, or
 *                  the surface is read-only and parents ordered for the day
 *                  (a PARENT-only campus: parent demand is the content).
 *  - 'order-now' → "Order now" — an orderable menu with nothing placed yet;
 *                  the obvious next action is to start the order.
 *  - 'hidden'    → no pill and an inert card — nothing to see or do: no
 *                  active menu, ordering closed with nothing placed, the
 *                  school marked the day NO_SERVICE, or the day's menu data
 *                  hasn't resolved.
 *
 * When the school's cart is empty, the week's first 'order-now' day doubles
 * as the primary (blue) CTA — the "click here to get started" affordance for
 * schools that don't yet know where to begin (`primaryOrderNowIndex`).
 */

export type DayCtaState = 'hidden' | 'details' | 'order-now' | 'service-details'

export interface DayCtaInput {
  /**
   * The day has an active menu with orderable items; `null` while the menus
   * query hasn't resolved (rendered the same as no menu — no pill flashes in
   * with the wrong copy while data loads, and a failed query degrades the
   * same way `CollapsedSummary`'s "No menu this day" does).
   */
  hasMenu: boolean | null
  /** The day already has submitted orders (`AlreadyOrdered.dayQty > 0`). */
  hasOrders: boolean
  /** Ordering for the day is closed — past, or its deadline has passed. */
  closed: boolean
  /** The whole surface is read-only (org SCHOOL_ORDER channel disabled). */
  readOnly: boolean
  /**
   * History mode: strictly past by the viewer clock, OR the day's cafeteria
   * service-completion rollup says complete. Always false on the cafeteria
   * lookahead surface (no service badges there).
   */
  history?: boolean
  /**
   * Service-relevant activity beyond school orders: committed parent
   * orders on the day, or a completed cafeteria service (register-only
   * days). Only consulted in history mode — a future day with parent
   * orders must not grow a "Show details" it never had.
   */
  historyActivity?: boolean
  /**
   * Committed parent orders on the day. Only consulted on a read-only
   * surface (org SCHOOL_ORDER channel off, or a PARENT-only campus), where
   * parent demand is the only thing to see: such a day stays reviewable
   * even once the school's window has closed. An orderable campus ignores
   * it, so a day with parent orders there still says "Order now".
   */
  parentOrders?: boolean
  /**
   * The school marked the day NO_SERVICE (PROD-1228). The submit gate rejects
   * it, so the card must never offer 'order-now' however healthy the menu
   * looks — a menu's own date window happily spans a term holiday.
   *
   * Deliberately does NOT suppress 'details' / 'service-details': a closure
   * authored after a school ordered must not strand the existing order.
   */
  noService?: boolean
}

export function dayCtaState({
  hasMenu,
  hasOrders,
  closed,
  readOnly,
  history = false,
  historyActivity = false,
  parentOrders = false,
  noService = false,
}: DayCtaInput): DayCtaState {
  if (history && (hasOrders || historyActivity)) {
    return 'service-details'
  }
  if (hasOrders || (readOnly && parentOrders)) {
    return 'details'
  }
  if (hasMenu !== true || closed || noService) {
    return 'hidden'
  }
  // Orderable day with nothing placed. A read-only surface may still view
  // the menu but must not advertise an action it can't take.
  return readOnly ? 'details' : 'order-now'
}

/**
 * Index of the day whose "Order now" pill renders as the primary (blue) CTA,
 * or -1. Only while the cart is empty — once lines exist, the cart's own
 * "Review and Checkout" is the primary action — and only once every day's
 * menu query has resolved: a primary that jumps from Tuesday to Monday as
 * queries land reads as a glitch.
 */
export function primaryOrderNowIndex(states: readonly DayCtaState[], cartEmpty: boolean, allResolved: boolean): number {
  if (!cartEmpty || !allResolved) {
    return -1
  }
  return states.indexOf('order-now')
}
